// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import {
  profileFormSchemaApi,
  PROFILE_FORM_TYPES,
  type FactFindSchema,
  type ProfileFormSchemaDTO,
  type ProfileFormType,
} from '../api/profileFormSchemaApi';
import FactFindSchemaEditor from '../components/factfind/FactFindSchemaEditor';

type Tab = 'default' | 'brokerage';

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  border: 'none',
  borderBottom: active ? '2px solid #1e40af' : '2px solid transparent',
  background: 'none',
  color: active ? '#1e40af' : '#6b7280',
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
});

/**
 * Admin page for living profile-form schemas (fact-find, risk attitude),
 * owned by the Resource Server. A form-type selector switches between form
 * types; within each, platform admins edit the global default and brokerage
 * admins edit a per-brokerage override. See
 * docs/specs/repeatable-profile-forms-prd.md.
 */
const ProfileFormsConfigPage: React.FC = () => {
  const [formType, setFormType] = useState<ProfileFormType>('FACT_FIND');
  const [tab, setTab] = useState<Tab>('default');

  const [defaultDto, setDefaultDto] = useState<ProfileFormSchemaDTO | null>(null);
  const [defaultErr, setDefaultErr] = useState<string | null>(null);
  const [savingDefault, setSavingDefault] = useState(false);

  const [brokerageId, setBrokerageId] = useState('');
  const [loadedBrokerageId, setLoadedBrokerageId] = useState('');
  const [brokerageDto, setBrokerageDto] = useState<ProfileFormSchemaDTO | null>(null);
  const [brokerageErr, setBrokerageErr] = useState<string | null>(null);
  const [savingOverride, setSavingOverride] = useState(false);

  const meta = PROFILE_FORM_TYPES.find((m) => m.type === formType)!;

  // (Re)load the default whenever the selected form type changes; reset override state.
  useEffect(() => {
    setDefaultDto(null);
    setDefaultErr(null);
    setBrokerageDto(null);
    setBrokerageErr(null);
    setLoadedBrokerageId('');
    profileFormSchemaApi.getDefault(formType)
      .then(setDefaultDto)
      .catch((e) => setDefaultErr(e instanceof Error ? e.message : 'Failed to load default schema'));
  }, [formType]);

  const saveDefault = async (schema: FactFindSchema) => {
    setSavingDefault(true);
    setDefaultErr(null);
    try {
      setDefaultDto(await profileFormSchemaApi.updateDefault(formType, schema));
    } catch (e) {
      setDefaultErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingDefault(false);
    }
  };

  const loadBrokerage = async () => {
    const id = brokerageId.trim();
    if (!id) return;
    setBrokerageErr(null);
    setBrokerageDto(null);
    try {
      const dto = await profileFormSchemaApi.getForBrokerage(formType, id);
      setBrokerageDto(dto);
      setLoadedBrokerageId(id);
    } catch (e) {
      setBrokerageErr(e instanceof Error ? e.message : 'Failed to load brokerage schema');
    }
  };

  const saveOverride = async (schema: FactFindSchema) => {
    setSavingOverride(true);
    setBrokerageErr(null);
    try {
      setBrokerageDto(await profileFormSchemaApi.updateOverride(formType, loadedBrokerageId, schema, true));
    } catch (e) {
      setBrokerageErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingOverride(false);
    }
  };

  const revertOverride = async () => {
    if (!window.confirm('Delete this brokerage override and revert to the global default?')) return;
    setSavingOverride(true);
    setBrokerageErr(null);
    try {
      await profileFormSchemaApi.deleteOverride(formType, loadedBrokerageId);
      setBrokerageDto(await profileFormSchemaApi.getForBrokerage(formType, loadedBrokerageId));
    } catch (e) {
      setBrokerageErr(e instanceof Error ? e.message : 'Revert failed');
    } finally {
      setSavingOverride(false);
    }
  };

  return (
    <div data-testid="profile-forms-config-page" style={{ maxWidth: 900 }}>
      <h1>Profile form schemas</h1>
      <p style={{ color: '#6b7280' }}>
        The question sets for the platform's living, versioned profile forms. Edit the platform
        default or a per-brokerage override; clients always answer against the effective schema.
        The schema in effect at submit time is snapshotted onto each submitted version.
      </p>

      <label style={{ display: 'block', marginTop: 12, marginBottom: 4, fontSize: 13, color: '#374151' }}>
        Form type
      </label>
      <select
        data-testid="form-type-select"
        value={formType}
        onChange={(e) => {
          setFormType(e.target.value as ProfileFormType);
          setTab('default');
        }}
        style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, minWidth: 220 }}
      >
        {PROFILE_FORM_TYPES.map((m) => (
          <option key={m.type} value={m.type}>{m.label}</option>
        ))}
      </select>
      <p style={{ color: '#6b7280', fontSize: 13, marginTop: 8 }}>{meta.description}</p>

      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginTop: 16 }}>
        <button type="button" style={tabStyle(tab === 'default')} data-testid="pf-tab-default"
          onClick={() => setTab('default')}>Platform default</button>
        <button type="button" style={tabStyle(tab === 'brokerage')} data-testid="pf-tab-brokerage"
          onClick={() => setTab('brokerage')}>Brokerage override</button>
      </div>

      <div style={{ marginTop: 16 }}>
        {tab === 'default' ? (
          defaultDto ? (
            <FactFindSchemaEditor
              key={`default-${formType}`}
              initialSchema={defaultDto.schema}
              source={defaultDto.source}
              version={defaultDto.version}
              saving={savingDefault}
              error={defaultErr}
              onSave={saveDefault}
            />
          ) : (
            <div data-testid="pf-default-loading">{defaultErr ?? 'Loading…'}</div>
          )
        ) : (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input
                type="text"
                data-testid="pf-brokerage-input"
                placeholder="Brokerage UUID"
                value={brokerageId}
                onChange={(e) => setBrokerageId(e.target.value)}
                style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 4, padding: '6px 8px' }}
              />
              <button type="button" data-testid="pf-brokerage-load" style={tabStyle(false)}
                onClick={loadBrokerage}>Load</button>
            </div>
            {brokerageErr && !brokerageDto && (
              <div data-testid="pf-brokerage-error" style={{ color: '#b91c1c' }}>{brokerageErr}</div>
            )}
            {brokerageDto && (
              <FactFindSchemaEditor
                key={`brokerage-${formType}-${loadedBrokerageId}`}
                initialSchema={brokerageDto.schema}
                source={brokerageDto.source}
                version={brokerageDto.version}
                saving={savingOverride}
                error={brokerageErr}
                onSave={saveOverride}
                onDelete={revertOverride}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileFormsConfigPage;
